import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const RANGE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];
const VIEW_MODE_OPTIONS = [
  { value: "auction", label: "By Auction" },
  { value: "weekly", label: "By Weekly" },
];

const METRIC_OPTIONS = [
  { value: "totalRegistrations", label: "Total Registrations", isPct: false, isMoney: false },
  { value: "fulfillmentNewUsersCount", label: "New Users", isPct: false, isMoney: false },
  { value: "fulfillmentShippingCount", label: "Shippers", isPct: false, isMoney: false },
  { value: "fulfillmentPickupCount", label: "Pickups", isPct: false, isMoney: false },
  { value: "totalLotsWon", label: "Sold Lots", isPct: false, isMoney: false },
  { value: "totalLotsPassed", label: "Passed Lots", isPct: false, isMoney: false },
  { value: "boughtLotsPct", label: "Sold Lots %", isPct: true, isMoney: false },
  { value: "passedLotsPct", label: "Passed Lots %", isPct: true, isMoney: false },
  { value: "totalHighValue", label: "High Bid ($)", isPct: false, isMoney: true },
  { value: "afRevenuePerLot", label: "Revenue Per Lot ($)", isPct: false, isMoney: true },
  { value: "totalViews", label: "Total Views", isPct: false, isMoney: false },
  { value: "totalWatches", label: "Total Watches", isPct: false, isMoney: false },
  { value: "totalBids", label: "Total Bids", isPct: false, isMoney: false },
  { value: "totalUniqueBidders", label: "Unique Bidders", isPct: false, isMoney: false },
];
const WEEKLY_AVG_METRIC_KEYS = new Set(["afRevenuePerLot", "boughtLotsPct", "passedLotsPct"]);

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toAbsInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
};

const asMs = (v) => {
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n;
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const monthKey = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const startOfWeekMs = (ms) => {
  if (!ms) return 0;
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7; // Monday start
  d.setDate(d.getDate() - diff);
  return d.getTime();
};
const endOfWeekMs = (weekStartMs) => {
  if (!weekStartMs) return 0;
  return weekStartMs + (6 * 24 * 60 * 60 * 1000);
};
const weekRangeLabel = (weekStartMs) => {
  if (!weekStartMs) return "No Week";
  const start = new Date(weekStartMs);
  const end = new Date(endOfWeekMs(weekStartMs));
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
};

const fmtInt = (v) => new Intl.NumberFormat().format(toAbsInt(v));
const fmtMoney = (v) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeNum(v));
const fmtPct = (v) => `${safeNum(v).toFixed(1)}%`;
const normalize = (v) => String(v || "").trim().toLowerCase();
const uniqSortedOptions = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
const isIndyLocation = (value = "") => {
  const safe = normalize(value).replace(/\s+/g, " ");
  return safe === "indy" || safe.includes("indianapolis");
};

const formatValue = (value, { isPct = false, isMoney = false } = {}) => {
  if (isPct) return fmtPct(value);
  if (isMoney) return fmtMoney(value);
  return fmtInt(value);
};

export default function AuctionWeeklyStatsPanel({ rows = [], primary = "#333999", locationMetadataRows = [] }) {
  const [rangeMode, setRangeMode] = useState("month");
  const [viewMode, setViewMode] = useState("auction");
  const [selectedMetric, setSelectedMetric] = useState("totalRegistrations");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(() => monthKey(Date.now()));
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const metadataLocationNames = useMemo(
    () => uniqSortedOptions((Array.isArray(locationMetadataRows) ? locationMetadataRows : []).map((row) => row?.name || "")),
    [locationMetadataRows]
  );
  const metadataLocationNameByKey = useMemo(() => {
    const map = new Map();
    metadataLocationNames.forEach((name) => {
      const key = normalize(name);
      if (!key || map.has(key)) return;
      map.set(key, name);
    });
    return map;
  }, [metadataLocationNames]);

  const orderedRows = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return [...base]
      .map((row) => {
        const endMs = asMs(row?.endMs || row?.row?.auctionEndAtMs || row?.row?.auctionEndAt || row?.row?.endAtMs || row?.row?.endAt);
        const labelDate = endMs ? new Date(endMs).toLocaleDateString() : "No End Date";
        const auctionName = String(row?.auctionName || row?.auctionId || "Untitled Auction").trim();
        const location = String(row?.location || "").trim();
        const pickupLocations = uniqSortedOptions(row?.pickupLocations || row?.row?.pickupLocations || []);
        const metadataMatchedPickupLocations = pickupLocations
          .map((name) => metadataLocationNameByKey.get(normalize(name)) || "")
          .filter(Boolean);
        const fallbackLocation = metadataLocationNameByKey.get(normalize(location)) || location;
        const rawFilterLocations = metadataMatchedPickupLocations.length
          ? metadataMatchedPickupLocations
          : uniqSortedOptions([fallbackLocation]);
        const nonIndyFilterLocations = rawFilterLocations.filter((name) => !isIndyLocation(name));
        const effectiveFilterLocations = nonIndyFilterLocations.length
          ? nonIndyFilterLocations
          : uniqSortedOptions(rawFilterLocations);
        const totalLots = toAbsInt(row?.totalLots);
        const totalRevenue = safeNum(row?.totalHighValue ?? row?.row?.lotsTotalHighBid ?? row?.row?.totalHighBid ?? row?.afGain);
        const afRevenuePerLot = totalLots > 0 ? totalRevenue / totalLots : 0;
        return {
          ...row,
          endMs,
          labelDate,
          auctionName,
          location,
          pickupLocations,
          filterLocations: effectiveFilterLocations,
          totalLots,
          totalRevenue,
          afRevenuePerLot,
        };
      })
      .sort((a, b) => {
        if (!a.endMs && !b.endMs) return String(a.auctionName).localeCompare(String(b.auctionName));
        if (!a.endMs) return 1;
        if (!b.endMs) return -1;
        return a.endMs - b.endMs;
      });
  }, [metadataLocationNameByKey, rows]);

  const locationOptions = useMemo(() => {
    const set = new Set();
    orderedRows.forEach((row) => {
      (Array.isArray(row?.filterLocations) ? row.filterLocations : []).forEach((loc) => {
        const safe = String(loc || "").trim();
        if (safe) set.add(safe);
      });
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [orderedRows]);

  const locationScopedRows = useMemo(() => {
    if (!selectedLocation || selectedLocation === "all") return orderedRows;
    const target = normalize(selectedLocation);
    return orderedRows.filter((row) =>
      (Array.isArray(row?.filterLocations) ? row.filterLocations : []).some((loc) => normalize(loc) === target)
    );
  }, [orderedRows, selectedLocation]);

  const filteredRows = useMemo(() => {
    if (rangeMode === "all") return locationScopedRows;

    if (rangeMode === "month") {
      const mk = String(selectedMonth || "").trim();
      if (!mk) return locationScopedRows;
      return locationScopedRows.filter((row) => row.endMs && monthKey(row.endMs) === mk);
    }

    const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;

    return locationScopedRows.filter((row) => {
      if (!row.endMs) return false;
      if (fromMs !== null && row.endMs < fromMs) return false;
      if (toMs !== null && row.endMs > toMs) return false;
      return true;
    });
  }, [locationScopedRows, rangeMode, selectedMonth, fromDate, toDate]);

  const metricMeta = useMemo(() => {
    return METRIC_OPTIONS.find((m) => m.value === selectedMetric) || METRIC_OPTIONS[0];
  }, [selectedMetric]);

  const weeklyRows = useMemo(() => {
    const grouped = new Map();
    filteredRows.forEach((row) => {
      if (!row?.endMs) return;
      const weekStart = startOfWeekMs(row.endMs);
      if (!grouped.has(weekStart)) {
        grouped.set(weekStart, {
          weekStart,
          weekLabel: weekRangeLabel(weekStart),
          rows: [],
        });
      }
      grouped.get(weekStart).rows.push(row);
    });

    return [...grouped.values()]
      .sort((a, b) => a.weekStart - b.weekStart)
      .map((week) => {
        const rowCount = week.rows.length;
        const metricSum = week.rows.reduce((sum, row) => sum + safeNum(row?.[selectedMetric]), 0);
        const metricValue = WEEKLY_AVG_METRIC_KEYS.has(selectedMetric)
          ? (rowCount > 0 ? metricSum / rowCount : 0)
          : metricSum;

        return {
          id: `week-${week.weekStart}`,
          name: `Week of ${new Date(week.weekStart).toLocaleDateString()}`,
          labelDate: week.weekLabel,
          xLabel: week.weekLabel,
          metricValue,
          rowCount,
        };
      });
  }, [filteredRows, selectedMetric]);

  const displayRows = useMemo(() => {
    if (viewMode === "weekly") return weeklyRows;
    return filteredRows.map((row, index) => {
      return {
        id: row.auctionId || String(index),
        name: row.auctionName,
        labelDate: row.labelDate,
        xLabel: row.labelDate,
        metricValue: safeNum(row?.[selectedMetric]),
      };
    });
  }, [viewMode, weeklyRows, filteredRows, selectedMetric]);

  const chartData = useMemo(() => {
    return displayRows.map((row) => ({
      id: row.id,
      xLabel: row.xLabel,
      metricValue: safeNum(row.metricValue),
    }));
  }, [displayRows]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.registrations += toAbsInt(row?.totalRegistrations);
        acc.newUsers += toAbsInt(row?.fulfillmentNewUsersCount);
        acc.soldLots += toAbsInt(row?.totalLotsWon);
        acc.passedLots += toAbsInt(row?.totalLotsPassed);
        acc.shippers += toAbsInt(row?.fulfillmentShippingCount);
        acc.pickups += toAbsInt(row?.fulfillmentPickupCount);
        acc.afRevenue += safeNum(row?.totalRevenue ?? row?.totalHighValue ?? row?.afGain);
        acc.totalLots += toAbsInt(row?.totalLots);
        return acc;
      },
      { registrations: 0, newUsers: 0, soldLots: 0, passedLots: 0, shippers: 0, pickups: 0, afRevenue: 0, totalLots: 0 }
    );
  }, [filteredRows]);

  const registrationBase = toAbsInt(totals.registrations);
  const lotsBase = toAbsInt(totals.soldLots) + toAbsInt(totals.passedLots);
  const newUsersPct = registrationBase > 0 ? (toAbsInt(totals.newUsers) / registrationBase) * 100 : 0;
  const shippersPct = registrationBase > 0 ? (toAbsInt(totals.shippers) / registrationBase) * 100 : 0;
  const pickupsPct = registrationBase > 0 ? (toAbsInt(totals.pickups) / registrationBase) * 100 : 0;
  const soldLotsPct = lotsBase > 0 ? (toAbsInt(totals.soldLots) / lotsBase) * 100 : 0;
  const passedLotsPct = lotsBase > 0 ? (toAbsInt(totals.passedLots) / lotsBase) * 100 : 0;
  const afRevenuePerLot = toAbsInt(totals.totalLots) > 0 ? safeNum(totals.afRevenue) / toAbsInt(totals.totalLots) : 0;
  const summaryCards = [
    {
      title: "Total Registrations",
      value: fmtInt(totals.registrations),
      subtitle: "All registrations in selected range",
    },
    {
      title: "New Users",
      value: `${fmtInt(totals.newUsers)} (${fmtPct(newUsersPct)})`,
      subtitle: "Count and share of registrations",
    },
    {
      title: "Shippers",
      value: `${fmtInt(totals.shippers)} (${fmtPct(shippersPct)})`,
      subtitle: "Requested shipping from registrations",
    },
    {
      title: "Pickups",
      value: `${fmtInt(totals.pickups)} (${fmtPct(pickupsPct)})`,
      subtitle: "Pickup share from registrations",
    },
    {
      title: "Sold Lots",
      value: `${fmtInt(totals.soldLots)} (${fmtPct(soldLotsPct)})`,
      subtitle: "Sell-through from sold vs passed lots",
    },
    {
      title: "Passed Lots",
      value: `${fmtInt(totals.passedLots)} (${fmtPct(passedLotsPct)})`,
      subtitle: "Unsold lot share in selected range",
    },
    {
      title: "Revenue Per Lot",
      value: fmtMoney(afRevenuePerLot),
      subtitle: `${fmtMoney(totals.afRevenue)} total / ${fmtInt(totals.totalLots)} lots`,
    },
  ];

  return (
    <Paper
      sx={{
        mt: 1.75,
        p: { xs: 1.25, md: 1.75 },
        borderRadius: 3,
        border: "1px solid rgba(51,57,153,0.18)",
        background: "linear-gradient(180deg,#ffffff 0%, #f8fafc 100%)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>Auction Weekly Stats</Typography>
        <Chip
          size="small"
          label={`${fmtInt(displayRows.length)} ${viewMode === "weekly" ? "weeks" : "auctions"} shown`}
          sx={{ borderRadius: 999, fontWeight: 900, color: primary, border: "1px solid rgba(51,57,153,0.22)", background: "#fff" }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <FormControl size="small" sx={{ minWidth: 180, background: "#fff" }}>
          <InputLabel id="auction-stats-view-mode-label">View Mode</InputLabel>
          <Select
            labelId="auction-stats-view-mode-label"
            label="View Mode"
            value={viewMode}
            onChange={(e) => setViewMode(String(e.target.value || "auction"))}
          >
            {VIEW_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220, background: "#fff" }}>
          <InputLabel id="auction-stats-location-label">Location</InputLabel>
          <Select
            labelId="auction-stats-location-label"
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(String(e.target.value || "all"))}
          >
            <MenuItem value="all">All Locations</MenuItem>
            {locationOptions.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180, background: "#fff" }}>
          <InputLabel id="auction-stats-range-label">Time Range</InputLabel>
          <Select
            labelId="auction-stats-range-label"
            label="Time Range"
            value={rangeMode}
            onChange={(e) => setRangeMode(String(e.target.value || "month"))}
          >
            {RANGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {rangeMode === "month" ? (
          <TextField
            size="small"
            type="month"
            label="Month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(String(e.target.value || ""))}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#fff" }}
          />
        ) : null}

        {rangeMode === "custom" ? (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              value={fromDate}
              onChange={(e) => setFromDate(String(e.target.value || ""))}
              InputLabelProps={{ shrink: true }}
              sx={{ background: "#fff" }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={toDate}
              onChange={(e) => setToDate(String(e.target.value || ""))}
              InputLabelProps={{ shrink: true }}
              sx={{ background: "#fff" }}
            />
          </>
        ) : null}

        <FormControl size="small" sx={{ minWidth: 260, background: "#fff" }}>
          <InputLabel id="auction-stats-metric-label">Chart Metric</InputLabel>
          <Select
            labelId="auction-stats-metric-label"
            label="Chart Metric"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(String(e.target.value || "totalRegistrations"))}
          >
            {METRIC_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {orderedRows.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No auction analytics data available yet.
        </Alert>
      ) : displayRows.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No data found in the selected range.
        </Alert>
      ) : (
        <>
          <Box sx={{ width: "100%", height: 320, mb: 1.5 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 64 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="xLabel" angle={-30} textAnchor="end" interval={0} height={84} tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => formatValue(value, { isPct: metricMeta.isPct, isMoney: metricMeta.isMoney })}
                />
                <RechartsTooltip
                  formatter={(value) => [
                    formatValue(value, { isPct: metricMeta.isPct, isMoney: metricMeta.isMoney }),
                    metricMeta.label,
                  ]}
                  labelFormatter={(label) => String(label)}
                />
                <Bar dataKey="metricValue" fill="#4f46e5" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
            {summaryCards.map((card, idx) => {
              const filled = idx % 2 === 0;
              return (
                <Grid item xs={12} sm={6} lg={4} key={card.title}>
                  <Card
                    elevation={2}
                    sx={{
                      border: `2px solid ${primary}`,
                      borderRadius: 3,
                      bgcolor: filled ? primary : "#fff",
                      color: filled ? "#fff" : primary,
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="overline" sx={{ opacity: 0.9, display: "block", lineHeight: 1.2 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                        {card.value}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                        {card.subtitle}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ border: "1px solid rgba(148,163,184,0.24)", borderRadius: 2, overflow: "hidden" }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.9,
                borderBottom: "1px solid rgba(148,163,184,0.24)",
                background: "linear-gradient(180deg,#ffffff 0%, #f8fafc 100%)",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>Showing: {metricMeta.label}</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 900 }}>{viewMode === "weekly" ? "Week" : "Auction"}</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>{viewMode === "weekly" ? "Date Range" : "End Date"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900 }}>
                    {metricMeta.label}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((row, idx) => (
                  <TableRow key={`${row.id || "row"}-${idx}`}>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap title={row.name}>
                        {row.name}
                      </Typography>
                      {viewMode === "weekly" && Number(row.rowCount) > 0 ? (
                        <Typography sx={{ color: "#64748b", fontSize: 12 }}>
                          {fmtInt(row.rowCount)} auctions
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.labelDate}</TableCell>
                    <TableCell align="right">
                      {formatValue(row?.metricValue, { isPct: metricMeta.isPct, isMoney: metricMeta.isMoney })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </Paper>
  );
}
